import logger from '@/app/utils/logger.ts';
import { CURRENT_BUILD_HASH, DEV_BUILD_HASH } from '@/app/utils/buildHash.ts';

export { CURRENT_BUILD_HASH };

/**
 * Stale-frontend detection.
 *
 * Rule: an error combined with an outdated build means a reload is required. Neither signal alone
 * forces anything — an outdated tab that still works keeps working (the user only gets a toast),
 * and an error on the current build is surfaced as a normal error.
 */

const MIN_CHECK_INTERVAL_MS = 30_000;

export const BUILD_RELOAD_STORAGE_KEY = 'build_reload_attempted';

/** How the outdated build was detected: by the periodic/visibility poll, or while handling an error. */
export type OutdatedSource = 'poll' | 'error';

export interface OutdatedState {
  outdated: boolean;
  /** True once the outdated build has been seen together with an error (5xx, crash, chunk failure). */
  detectedViaError: boolean;
}

type OutdatedListener = (state: OutdatedState) => void;

let state: OutdatedState = { outdated: false, detectedViaError: false };
let lastFetchAt = Number.NEGATIVE_INFINITY;
let inFlight: Promise<boolean> | null = null;
const listeners = new Set<OutdatedListener>();

/** Fetches the build hash of the currently deployed frontend. Returns null on any failure. */
export async function fetchServerBuildHash(): Promise<string | null> {
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!response.ok) {
      return null;
    }

    const data: unknown = await response.json();
    const buildHash = (data as { buildHash?: unknown } | null)?.buildHash;

    return typeof buildHash === 'string' && buildHash.length > 0 ? buildHash : null;
  } catch {
    return null;
  }
}

const markOutdated = (source: OutdatedSource) => {
  const escalateToError = source === 'error' && !state.detectedViaError;
  if (state.outdated && !escalateToError) {
    return;
  }

  state = { outdated: true, detectedViaError: state.detectedViaError || source === 'error' };
  listeners.forEach((listener) => listener(state));
};

/**
 * Checks whether a newer build has been deployed than the one running in this tab.
 *
 * - Throttled: at most one fetch per 30s; calls inside that window resolve to the last known state.
 * - Concurrent callers share the in-flight request.
 * - Once outdated has been seen it is memoized (a tab never becomes "current" again without a reload).
 * - Resolves false when the check itself fails, and always under local dev (no version.json).
 */
export function checkIsOutdated(source: OutdatedSource = 'error'): Promise<boolean> {
  if (state.outdated) {
    markOutdated(source);
    return Promise.resolve(true);
  }

  if (CURRENT_BUILD_HASH === DEV_BUILD_HASH) {
    return Promise.resolve(false);
  }

  if (inFlight) {
    return inFlight.then((outdated) => {
      if (outdated) {
        markOutdated(source);
      }
      return outdated;
    });
  }

  if (Date.now() - lastFetchAt < MIN_CHECK_INTERVAL_MS) {
    return Promise.resolve(false);
  }

  lastFetchAt = Date.now();
  inFlight = fetchServerBuildHash()
    .then((serverBuildHash) => {
      const outdated = serverBuildHash != null && serverBuildHash !== CURRENT_BUILD_HASH;
      if (outdated) {
        markOutdated(source);
      }
      return outdated;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

export function getOutdatedState(): OutdatedState {
  return state;
}

/** Subscribes to outdated-state changes. Returns an unsubscribe function. */
export function subscribeOutdated(listener: OutdatedListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Reloads the page at most once per browser session (guarded by sessionStorage) to pick up a new
 * build. Returns true if a reload was triggered, false if the guard was already set (or storage
 * is unavailable, in which case we refuse to reload to avoid a reload loop).
 */
export function reloadOnce(reason: string): boolean {
  try {
    if (sessionStorage.getItem(BUILD_RELOAD_STORAGE_KEY)) {
      logger.warn('[buildVersion] Reload already attempted this session, not reloading again', {
        reason,
        currentBuildHash: CURRENT_BUILD_HASH
      });
      return false;
    }
    sessionStorage.setItem(BUILD_RELOAD_STORAGE_KEY, 'true');
  } catch {
    return false;
  }

  logger.warn('[buildVersion] Reloading page to load the new build', { reason, currentBuildHash: CURRENT_BUILD_HASH });
  window.location.reload();
  return true;
}

/** Clears the reload guard. Call only after something from the current build loaded successfully. */
export function clearReloadGuard(): void {
  try {
    sessionStorage.removeItem(BUILD_RELOAD_STORAGE_KEY);
  } catch {
    // ignore
  }
}
